
/** Paths target a single Binated Quick Proof SKU and never mutate a Sales Order cart. */
export const quickProofProductUrlPath = (skuIndex) =>
  ["data", "sales_order", 0, "purchase_order", 0, "sku", skuIndex, "product_url"];

export const quickProofPlacementUrlsPath = (skuIndex, decorationIndex, placementIndex) =>
  ["data", "sales_order", 0, "purchase_order", 0, "sku", skuIndex,
    "decoration", decorationIndex, "placements", placementIndex, "url"];

export const getQuickProofPlacementUrls = (sku = {}, decorationIndex, placementIndex) => {
  const urls = sku?.decoration?.[decorationIndex]?.placements?.[placementIndex]?.url;
  return Array.isArray(urls) ? urls : [];
};

export const getQuickProofArtworkSlots = (sku = {}) =>
  (Array.isArray(sku?.decoration) ? sku.decoration : []).flatMap((decoration, decorationIndex) =>
    (Array.isArray(decoration?.placements) ? decoration.placements : []).flatMap((placement, placementIndex) => {
      const urls = Array.isArray(placement?.url) ? placement.url : [];
      return (urls.length ? urls : [""]).map((url, urlIndex) => ({
        key: [decorationIndex, placementIndex, urlIndex].join(":"),
        decorationIndex,
        placementIndex,
        urlIndex,
        url: String(url || "").trim(),
        placementName: String(placement?.placement_name || decoration?.decoration_type || ("Placement " + (placementIndex + 1))),
      }));
    })
  );

/** A replacement changes only the target URL, retaining all the other source files. */
export const replaceQuickProofArtworkUrl = (currentUrls, urlIndex, url) => {
  const next = Array.isArray(currentUrls) ? [...currentUrls] : [];
  if (!Number.isSafeInteger(urlIndex) || urlIndex < 0 || urlIndex > 100) return next;
  while (next.length <= urlIndex) next.push("");
  next[urlIndex] = String(url || "").trim();
  return next;
};

export const restoreQuickProofArtworkUrl = (currentUrls, automaticUrls, urlIndex) => {
  const next = Array.isArray(currentUrls) ? [...currentUrls] : [];
  const originals = Array.isArray(automaticUrls) ? automaticUrls : [];
  if (!Number.isSafeInteger(urlIndex) || urlIndex < 0 || urlIndex >= next.length) return next;
  if (urlIndex < originals.length) next[urlIndex] = originals[urlIndex];
  else next.splice(urlIndex, 1);
  return next;
};

/** AI/EPS originals belong in the Binated payload; their optional PNG previews do not. */
export const getQuickProofOriginalFileUrl = (file = {}) => String(
  typeof file === "string" ? file :
    file?.originalUrl || file?.originalFileUrl || file?.filePath || ""
).trim();

export const getQuickProofFileName = (url = "") => {
  const raw = String(url || "").trim();
  if (!raw) return "No artwork";
  try {
    const parsed = new URL(raw);
    return decodeURIComponent(parsed.pathname.split("/").filter(Boolean).pop() || "File");
  } catch (_) {
    return raw.split(/[/?#]/).filter(Boolean).pop() || "File";
  }
};

export const getQuickProofFileExtension = (url = "") => {
  const match = getQuickProofFileName(url).match(/\.([a-z0-9]{1,8})$/i);
  return match ? match[1].toUpperCase() : "FILE";
};

/**
 * The shared product-image helper treats all /customer-files/ URLs as images.
 * AI, EPS, PDF and other design originals must instead display a file tile.
 */
export const isQuickProofImageCandidate = (url = "") => {
  const raw = String(url || "").trim();
  if (!raw) return false;
  const path = raw.split(/[?#]/)[0];
  const name = path.split("/").filter(Boolean).pop() || "";
  const match = name.match(/\.([a-z0-9]+)$/i);
  const imageExtension = match && /^(png|jpe?g|gif|webp|svg|bmp)$/i.test(match[1]);
  if (/\/customer-files\//i.test(path)) return Boolean(imageExtension);
  return match ? Boolean(imageExtension) : true; // ASI's media IDs can be extensionless.
};